import { useEffect, useState } from "react";
import {
    FiUsers,
    FiCreditCard,
    FiSearch,
    FiLoader,
    FiImage,
    FiFolder,
} from "react-icons/fi";
import { useDebouncedCallback } from "use-debounce";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchSmileClients } from "../../store/smileDesign/smileClientsSlice";
import TablePagination from "../../components/smileDesign/TablePagination";

const StatCard = ({
    label,
    value,
    icon,
}: {
    label: string;
    value: number | string;
    icon: React.ReactNode;
}) => (
    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
        <div className="flex items-center justify-between">
            <div>
                <p className="text-[#8b8f8d] text-sm">{label}</p>
                <h2 className="text-3xl font-bold text-white mt-2">{value}</h2>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
                {icon}
            </div>
        </div>
    </div>
);

const ClientManagement = () => {
    const dispatch = useAppDispatch();
    const { clients, stats, pagination, loading } = useAppSelector(
        (state) => state.smileClients
    );

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const loadClients = (overrides?: { page?: number; search?: string }) => {
        dispatch(
            fetchSmileClients({
                page: overrides?.page ?? page,
                search: (overrides?.search ?? search) || undefined,
            })
        );
    };

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setPage(1);
        loadClients({ page: 1, search: value });
    }, 400);

    useEffect(() => {
        loadClients();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = (value: string) => {
        setSearch(value);
        debouncedSearch(value);
    };

    return (
        <div className="min-h-screen bg-[#080808] text-white p-4 sm:p-6">
            <div className="w-full">
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5 mb-8">
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                            Client Management
                        </h1>
                        <p className="text-[#8b8f8d] mt-2 text-sm sm:text-base">
                            Clients with Smile Portal access, their generations,
                            cases and credit balances.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
                    <StatCard
                        label="Smile Clients"
                        value={stats?.totalClients ?? clients.length}
                        icon={<FiUsers className="text-2xl" />}
                    />
                    <StatCard
                        label="Total Generations"
                        value={stats?.totalGenerations ?? 0}
                        icon={<FiImage className="text-2xl" />}
                    />
                    <StatCard
                        label="Total Cases"
                        value={stats?.totalCases ?? 0}
                        icon={<FiFolder className="text-2xl" />}
                    />
                    <StatCard
                        label="Total Credits"
                        value={stats?.totalCredits ?? 0}
                        icon={<FiCreditCard className="text-2xl" />}
                    />
                </div>

                <div className="relative mb-6">
                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b6f6d]" />
                    <input
                        type="text"
                        placeholder="Search clients by token..."
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="w-full bg-[#101211] border border-[#1f2221] rounded-2xl py-3 pl-12 pr-4 text-white placeholder:text-[#6b6f6d] focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                </div>

                {loading && (
                    <div className="flex justify-center py-20">
                        <FiLoader className="animate-spin text-cyan-400 text-4xl" />
                    </div>
                )}

                {!loading && clients.length === 0 && (
                    <div className="text-center py-20 rounded-3xl border border-[#1f2221] bg-[#101211]">
                        <FiUsers className="text-5xl text-[#3a3d3c] mx-auto mb-4" />
                        <h3 className="text-xl font-semibold">
                            No clients found
                        </h3>
                    </div>
                )}

                {/* Desktop table */}
                {!loading && clients.length > 0 && (
                    <div className="hidden lg:block bg-[#101211] border border-[#1f2221] rounded-3xl overflow-hidden shadow-2xl">
                        <table className="w-full">
                            <thead className="bg-[#080808] border-b border-[#1f2221]">
                                <tr>
                                    <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                        Token
                                    </th>
                                    <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                        Email
                                    </th>
                                    <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                        Role
                                    </th>
                                    <th className="px-6 py-5 text-center text-[#b7bab8] font-semibold">
                                        Generations
                                    </th>
                                    <th className="px-6 py-5 text-center text-[#b7bab8] font-semibold">
                                        Cases
                                    </th>
                                    <th className="px-6 py-5 text-center text-[#b7bab8] font-semibold">
                                        Credits
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {clients.map((client) => (
                                    <tr
                                        key={client.user_id}
                                        className="border-b border-[#1f2221] hover:bg-[#151716] transition-all"
                                    >
                                        <td className="px-6 py-5">
                                            <span className="font-semibold font-mono bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 px-3 py-1.5 rounded-lg text-sm">
                                                {client.token || "—"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5 text-[#b7bab8]">
                                            {client.email}
                                        </td>
                                        <td className="px-6 py-5 text-[#8b8f8d]">
                                            {client.role}
                                        </td>
                                        <td className="px-6 py-5 text-center font-semibold text-white">
                                            {client.generations}
                                        </td>
                                        <td className="px-6 py-5 text-center font-semibold text-white">
                                            {client.cases}
                                        </td>
                                        <td className="px-6 py-5 text-center">
                                            <span className="bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 px-4 py-2 rounded-full text-sm whitespace-nowrap">
                                                {client.smile_credits} Credits
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <TablePagination
                            pagination={pagination}
                            onPageChange={(nextPage) => {
                                setPage(nextPage);
                                loadClients({ page: nextPage });
                            }}
                            itemLabel="clients"
                        />
                    </div>
                )}

                {/* Mobile cards */}
                {!loading && clients.length > 0 && (
                    <div className="lg:hidden space-y-4">
                        {clients.map((client) => (
                            <div
                                key={client.user_id}
                                className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <h3 className="font-semibold font-mono text-cyan-400">
                                            {client.token || "—"}
                                        </h3>
                                        <p className="text-[#8b8f8d] text-sm break-all mt-1">
                                            {client.email}
                                        </p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                                    <div className="bg-[#080808] border border-[#1f2221] rounded-xl py-3">
                                        <p className="text-[#6b6f6d] text-xs">
                                            Generations
                                        </p>
                                        <p className="font-semibold mt-1">
                                            {client.generations}
                                        </p>
                                    </div>
                                    <div className="bg-[#080808] border border-[#1f2221] rounded-xl py-3">
                                        <p className="text-[#6b6f6d] text-xs">
                                            Cases
                                        </p>
                                        <p className="font-semibold mt-1">
                                            {client.cases}
                                        </p>
                                    </div>
                                    <div className="bg-[#080808] border border-[#1f2221] rounded-xl py-3">
                                        <p className="text-[#6b6f6d] text-xs">
                                            Credits
                                        </p>
                                        <p className="font-semibold mt-1 text-cyan-400">
                                            {client.smile_credits}
                                        </p>
                                    </div>
                                </div>
                                <p className="text-[#6b6f6d] text-xs mt-3">
                                    Role: {client.role}
                                </p>
                            </div>
                        ))}
                        <TablePagination
                            pagination={pagination}
                            onPageChange={(nextPage) => {
                                setPage(nextPage);
                                loadClients({ page: nextPage });
                            }}
                            itemLabel="clients"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientManagement;
